import {
	HTTP_INTERCEPTORS,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpHeaders,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { TranslateService } from '@ngx-translate/core';
import { ApiErrorResponse } from 'app/interfaces/error.interface';
import { environment } from 'environments/environment';
import { from, of, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, concatMap, first } from 'rxjs/operators';

import package_json from '../../../package.json';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private afAuth: AngularFireAuth,
		private translate: TranslateService
	) {}

	// Generate headers for HTTP access.
	private generateHeaders(
		req: HttpRequest<any>,
		setXVersion = false
	): HttpHeaders {
		let authHeaders = new HttpHeaders();
		authHeaders = authHeaders.set('Accept', 'application/json');
		authHeaders = authHeaders.set('Content-Type', 'application/json');
		authHeaders = authHeaders.set(
			'Accept-Language',
			this.translate.currentLang
		);

		if (req.method !== 'OPTIONS') {
			if (window.location.hostname.includes('localhost')) {
				req.headers.append('Access-Control-Allow-Origin', '*');
			}
		}
		if (setXVersion) {
			authHeaders = authHeaders.set('X-Version', package_json.version);
		}

		if (req.headers) {
			for (const key of req.headers.keys()) {
				authHeaders = authHeaders.set(key, req.headers.getAll(key));
			}
		}

		return authHeaders;
	}

	private getApiAddress(): string {
		return environment.url;
	}

	private getFirebaseToken(): Observable<string | null> {
		return this.afAuth.user.pipe(
			concatMap((user) => {
				if (!user) {
					return of(null);
				} else {
					return from(user.getIdToken());
				}
			})
		);
	}

	intercept(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (req.url.startsWith('.')) {
			return next.handle(req);
		}

		// Check if the request url is absolute
		if (req.url.match(/^http/) && !req.url.includes('services.')) {
			const modified = req.clone({
				headers: req.headers,
				url: req.url,
			});
			return next.handle(modified);
		}

		const apiUrl = req.url.includes('services.')
			? req.url
			: this.getApiAddress() + req.url;
		const headers = this.generateHeaders(req, req.url.includes('services.'));

		return this.getFirebaseToken().pipe(
			first(),
			concatMap((token) => {
				if (token) {
					const modified = req.clone({
						headers: headers.append('Authorization', `Bearer ${token}`),
						url: apiUrl,
					});
					return next.handle(modified).pipe(
						catchError((err: HttpErrorResponse) => {
							return throwError(new ApiErrorResponse(err));
						})
					);
				} else {
					const modified = req.clone({
						headers: headers.append('Authorization', `unknown`),
						url: apiUrl,
					});
					return next.handle(modified).pipe(
						catchError((err: HttpErrorResponse) => {
							return throwError(new ApiErrorResponse(err));
						})
					);
				}
			})
		);
	}
}

@NgModule({
	providers: [
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true,
		},
	],
})
export class AuthInterceptorModule {}
